import { useApi } from "~/composables/useApi";
import { useToast } from "~/composables/useToast";
import type { Note, NoteCreateBody, NoteListQuery, NoteUpdateBody } from "@asap/shared/schemas/note/crud";
import type {
  Body as NoteSummarizeContentBody,
  Response as NoteSummarizeContentResponse,
} from "@asap/shared/schemas/note/summarize-content";

/** The note was deleted meanwhile, e.g. the blank note of an unanswered call. */
export class NoteNotFoundError extends Error {}

export const useNotes = () => {
  const { invoke } = useApi();
  const { addToastError } = useToast();
  const { t } = useI18n();

  async function createNote(note: NoteCreateBody): Promise<Note> {
    const result = await invoke({ path: "notes", method: "POST", body: note });
    if (!result.success) throw new Error(result.error.message);
    return result.data;
  }

  async function removeNote(id: string): Promise<void> {
    const result = await invoke({ path: "notes/:noteId", method: "DELETE", params: { noteId: id } });
    if (!result.success) {
      addToastError({ description: t("notes.errors.remove") });
      throw new Error(result.error.message);
    }
  }

  async function updateNote(id: string, updates: NoteUpdateBody): Promise<Note> {
    const result = await invoke({ path: "notes/:noteId", method: "PATCH", params: { noteId: id }, body: updates });
    if (!result.success) {
      if (result.status === 404) throw new NoteNotFoundError(result.error.message);
      addToastError({ description: t("notes.errors.update") });
      throw new Error(result.error.message);
    }
    return result.data;
  }

  async function getNote(id: string): Promise<Note> {
    const result = await invoke({ path: "notes/:noteId", method: "GET", params: { noteId: id } });
    if (!result.success) throw new Error(result.error.message);
    return result.data;
  }

  async function getNotes(filters?: NoteListQuery): Promise<Note[]> {
    const query: Record<string, string> = {};

    if (filters?.talent_id) query.talent_id = filters.talent_id;
    if (filters?.mission_id) query.mission_id = filters.mission_id;
    if (filters?.contract_id) query.contract_id = filters.contract_id;
    if (filters?.contract_ids?.length) query.contract_ids = filters.contract_ids.join(",");
    if (filters?.company_id) query.company_id = filters.company_id;
    if (filters?.contact_id) query.contact_id = filters.contact_id;
    if (filters?.onboarding_request_id) query.onboarding_request_id = filters.onboarding_request_id;
    if (filters?.medical_checkup_request_id) query.medical_checkup_request_id = filters.medical_checkup_request_id;
    if (filters?.qualification_training_request_id)
      query.qualification_training_request_id = filters.qualification_training_request_id;

    const result = await invoke({ path: "notes", method: "GET", query });
    if (!result.success) throw new Error(result.error.message);
    return result.data;
  }

  const readBlobAsDataUrl = (blob: Blob) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () =>
        typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("Audio could not be read"));
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  async function transcribeAudio(audio: Blob): Promise<string> {
    const result = await invoke({
      path: "note/transcribe-audio",
      method: "POST",
      body: { audio_base64: await readBlobAsDataUrl(audio), mime_type: audio.type },
    });
    if (!result.success) throw new Error(result.error.clientMessage || result.error.message);
    return result.data.transcription;
  }

  async function summarizeNoteContent(body: NoteSummarizeContentBody): Promise<NoteSummarizeContentResponse> {
    const result = await invoke({ path: "note/summarize-content", method: "POST", body });
    if (!result.success) throw new Error(result.error.message);
    return result.data;
  }

  async function generateNoteTitle(content: string): Promise<string> {
    const result = await invoke({ path: "note/generate-title", method: "POST", body: { content } });
    if (!result.success) throw new Error(result.error.message);
    return result.data.title;
  }

  return {
    createNote,
    removeNote,
    updateNote,
    getNote,
    getNotes,
    transcribeAudio,
    summarizeNoteContent,
    generateNoteTitle,
  };
};
